import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { ticketTypesService } from '../../services/ticketTypesService';
import ResourceSidebar from '../Shared/ResourceSidebar';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../Venues/VenuesPage.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const TicketTypesPage = () => {
  const navigate = useNavigate();
  const [ticketTypes, setTicketTypes] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [contextMenu, setContextMenu] = useState(null);
  const [aliasPanel, setAliasPanel] = useState(null);
  const [aliasInput, setAliasInput] = useState('');
  const [aliasLoading, setAliasLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  const [mergePanel, setMergePanel] = useState(null);
  const [mergeQuery, setMergeQuery] = useState('');
  const [mergeResults, setMergeResults] = useState([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState('');

  const loadTicketTypes = async (qValue = '') => {
    setLoading(true);
    setError('');
    const result = await ticketTypesService.listTicketTypes({
      q: qValue || undefined,
      limit: 5000,
    });

    if (result.success) {
      setTicketTypes(result.items || []);
      setTotalCount(result.totalCount || 0);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTicketTypes('');
  }, []);

  useEffect(() => {
    if (!contextMenu) {
      return undefined;
    }

    const closeMenu = () => setContextMenu(null);
    window.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);

    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [contextMenu]);

  useEffect(() => {
    if (!mergePanel || !mergeQuery.trim()) {
      setMergeResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setMergeLoading(true);
      const result = await ticketTypesService.listTicketTypes({
        q: mergeQuery.trim(),
        limit: 20,
      });
      setMergeLoading(false);

      if (!result.success) {
        setMergeError(result.error);
        setMergeResults([]);
        return;
      }

      setMergeError('');
      setMergeResults((result.items || []).filter((item) => item.ticket_type_id !== mergePanel.ticketType.ticket_type_id));
    }, 250);

    return () => clearTimeout(timer);
  }, [mergePanel, mergeQuery]);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Ticket Type',
      field: 'name',
      minWidth: 300,
      flex: 1.8,
      pinned: 'left',
    },
    {
      headerName: 'Aliases',
      field: 'alias_count',
      minWidth: 120,
    },
    {
      headerName: 'Events',
      field: 'event_count',
      minWidth: 120,
    },
    {
      headerName: 'Purchases',
      field: 'purchase_count',
      minWidth: 120,
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
  }), []);

  const handleFilter = (event) => {
    event.preventDefault();
    loadTicketTypes(query.trim());
  };

  const openCreateModal = () => {
    setCreateName('');
    setCreateError('');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (createSaving) return;
    setShowCreateModal(false);
    setCreateError('');
  };

  const handleCreateTicketType = async (event) => {
    event.preventDefault();
    const name = createName.trim();

    if (!name) {
      setCreateError('Ticket type name is required.');
      return;
    }

    setCreateSaving(true);
    setCreateError('');
    const result = await ticketTypesService.createTicketType({ name });
    setCreateSaving(false);

    if (!result.success) {
      setCreateError(result.error);
      return;
    }

    setTicketTypes((current) => [result.item, ...current]);
    setTotalCount((current) => current + 1);
    setMessage(result.message || 'Ticket type created.');
    setError('');
    setShowCreateModal(false);
  };

  const openAliasPanel = async (ticketType) => {
    if (!ticketType?.ticket_type_id) return;
    setContextMenu(null);
    setAliasPanel({
      ticketType,
      aliases: [],
      error: '',
    });
    setAliasInput('');
    setAliasLoading(true);
    const result = await ticketTypesService.listAliases(ticketType.ticket_type_id);
    setAliasLoading(false);

    if (!result.success) {
      setAliasPanel((current) => current ? { ...current, error: result.error } : current);
      return;
    }

    setAliasPanel((current) => current ? {
      ...current,
      aliases: result.items || [],
      error: '',
    } : current);
  };

  const handleAddAlias = async (event) => {
    event.preventDefault();
    if (!aliasPanel?.ticketType?.ticket_type_id || !aliasInput.trim()) return;

    setAliasLoading(true);
    const result = await ticketTypesService.addAlias(aliasPanel.ticketType.ticket_type_id, aliasInput.trim());
    setAliasLoading(false);

    if (!result.success) {
      setAliasPanel((current) => current ? { ...current, error: result.error } : current);
      return;
    }

    setAliasInput('');
    setMessage(result.message || 'Alias added.');
    setAliasPanel((current) => current ? {
      ...current,
      aliases: [...current.aliases, result.item],
      error: '',
    } : current);
    setTicketTypes((current) => current.map((item) => (
      item.ticket_type_id === aliasPanel.ticketType.ticket_type_id
        ? { ...item, alias_count: (item.alias_count || 0) + 1 }
        : item
    )));
  };

  const openMergePanel = (ticketType) => {
    if (!ticketType?.ticket_type_id) return;
    setContextMenu(null);
    setMergePanel({ ticketType, saving: false });
    setMergeQuery('');
    setMergeResults([]);
    setMergeError('');
  };

  const closeMergePanel = () => {
    if (mergePanel?.saving) return;
    setMergePanel(null);
    setMergeQuery('');
    setMergeResults([]);
    setMergeError('');
  };

  const handleMergeTicketType = async (targetTicketType) => {
    if (!mergePanel?.ticketType?.ticket_type_id || !targetTicketType?.ticket_type_id) return;

    const confirmed = window.confirm(`Merge "${mergePanel.ticketType.name}" into "${targetTicketType.name}"?`);
    if (!confirmed) return;

    setMergePanel((current) => current ? { ...current, saving: true } : current);
    setMergeError('');
    const result = await ticketTypesService.mergeTicketType(mergePanel.ticketType.ticket_type_id, targetTicketType.ticket_type_id);

    if (!result.success) {
      setMergePanel((current) => current ? { ...current, saving: false } : current);
      setMergeError(result.error);
      return;
    }

    setTicketTypes((current) => current
      .filter((item) => item.ticket_type_id !== result.mergedTicketTypeId)
      .map((item) => (item.ticket_type_id === result.item.ticket_type_id ? result.item : item)));
    setTotalCount((current) => Math.max(0, current - 1));
    setMessage(result.message || 'Ticket type merged.');
    setError('');
    closeMergePanel();
  };

  const handleCellContextMenu = (params) => {
    if (!params.data?.ticket_type_id) {
      setContextMenu(null);
      return;
    }

    params.event.preventDefault();
    setContextMenu({
      x: params.event.clientX,
      y: params.event.clientY,
      ticketType: params.data,
    });
  };

  return (
    <div className="venues-page resource-page-with-sidebar">
      <ResourceSidebar />
      <header className="venues-header">
        <div className="venues-header-content">
          <HeaderNewTabButton />
          <h1>Ticket Types</h1>
          <div className="venues-header-actions">
            <button className="btn btn-primary" type="button" onClick={openCreateModal}>
              Add Ticket Type
            </button>
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/user')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="venues-main">
        <form className="venues-filter-form" onSubmit={handleFilter}>
          <div className="form-group">
            <label htmlFor="ticket-types-query">Search</label>
            <input
              id="ticket-types-query"
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ticket type or alias..."
            />
          </div>
          <button className="btn btn-primary" type="submit">
            Apply
          </button>
        </form>

        <div className="venues-summary">
          <strong>Showing:</strong> {ticketTypes.length} ticket types
          {!!totalCount && <span className="venues-summary-tag">Total: {totalCount}</span>}
        </div>

        {error && <div className="notification error">{error}</div>}
        {message && <div className="notification success">{message}</div>}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading ticket types...</p>
          </div>
        ) : (
          <div className="venues-grid-shell">
            <div className="ag-theme-quartz-dark venues-ag-grid">
              <AgGridReact
                theme="legacy"
                rowData={ticketTypes}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                animateRows={true}
                alwaysShowHorizontalScroll={true}
                pagination={true}
                paginationPageSize={100}
                getRowId={(params) => params.data.ticket_type_id}
                onCellContextMenu={handleCellContextMenu}
                preventDefaultOnContextMenu
                onCellDoubleClicked={(params) => openAliasPanel(params.data)}
              />
            </div>
          </div>
        )}

        {contextMenu && (
          <div
            className="venues-context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(event) => event.stopPropagation()}
          >
            <button className="venues-context-menu-item" type="button" onClick={() => openAliasPanel(contextMenu.ticketType)}>
              Show Aliases
            </button>
            <button className="venues-context-menu-item" type="button" onClick={() => openAliasPanel(contextMenu.ticketType)}>
              Add Alias
            </button>
            <button className="venues-context-menu-item" type="button" onClick={() => openMergePanel(contextMenu.ticketType)}>
              Merge Into...
            </button>
          </div>
        )}

        {aliasPanel && (
          <aside className="venues-alias-panel">
            <div className="venues-alias-panel-header">
              <div>
                <h2>{aliasPanel.ticketType.name}</h2>
                <p>{aliasPanel.aliases.length} aliases</p>
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => setAliasPanel(null)}>
                Close
              </button>
            </div>

            <form className="venues-alias-form" onSubmit={handleAddAlias}>
              <input
                type="text"
                value={aliasInput}
                onChange={(event) => setAliasInput(event.target.value)}
                placeholder="Add alias..."
                disabled={aliasLoading}
              />
              <button className="btn btn-primary" type="submit" disabled={aliasLoading || !aliasInput.trim()}>
                Add
              </button>
            </form>

            {aliasPanel.error && <div className="notification error">{aliasPanel.error}</div>}
            {aliasLoading && <div className="venues-alias-empty">Loading aliases...</div>}
            {!aliasLoading && !aliasPanel.aliases.length && (
              <div className="venues-alias-empty">No aliases yet.</div>
            )}
            {!aliasLoading && !!aliasPanel.aliases.length && (
              <div className="venues-alias-list">
                {aliasPanel.aliases.map((alias) => (
                  <div className="venues-alias-row" key={alias.ticket_type_mapping_id}>
                    <strong>{alias.alias_name}</strong>
                    <span>{alias.source || '-'}</span>
                  </div>
                ))}
              </div>
            )}
          </aside>
        )}

        {mergePanel && (
          <div className="venues-modal-backdrop" onMouseDown={closeMergePanel}>
            <section className="venues-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="venues-modal-header">
                <div>
                  <h2>Merge Ticket Type</h2>
                  <p className="venues-modal-subtitle">{mergePanel.ticketType.name}</p>
                </div>
                <button className="venues-modal-close" type="button" onClick={closeMergePanel} disabled={mergePanel.saving}>
                  x
                </button>
              </div>

              <div className="venues-merge-body">
                <div className="form-group">
                  <label htmlFor="ticket-type-merge-query">Merge Into</label>
                  <input
                    id="ticket-type-merge-query"
                    type="text"
                    value={mergeQuery}
                    onChange={(event) => setMergeQuery(event.target.value)}
                    placeholder="Search target ticket type..."
                    disabled={mergePanel.saving}
                    autoFocus
                  />
                </div>

                {mergeError && <div className="notification error">{mergeError}</div>}
                {mergeLoading && <div className="venues-alias-empty">Searching ticket types...</div>}
                {!mergeLoading && mergeQuery.trim() && !mergeResults.length && (
                  <div className="venues-alias-empty">No target ticket types found.</div>
                )}
                {!mergeLoading && !!mergeResults.length && (
                  <div className="venues-merge-results">
                    {mergeResults.map((ticketType) => (
                      <button
                        key={ticketType.ticket_type_id}
                        className="venues-merge-result"
                        type="button"
                        onClick={() => handleMergeTicketType(ticketType)}
                        disabled={mergePanel.saving}
                      >
                        <strong>{ticketType.name}</strong>
                        <span>{`${ticketType.event_count || 0} events | ${ticketType.purchase_count || 0} purchases | ${ticketType.alias_count || 0} aliases`}</span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="venues-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeMergePanel} disabled={mergePanel.saving}>
                    Cancel
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}

        {showCreateModal && (
          <div className="venues-modal-backdrop" onMouseDown={closeCreateModal}>
            <section className="venues-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="venues-modal-header">
                <h2>Add Ticket Type</h2>
                <button className="venues-modal-close" type="button" onClick={closeCreateModal} disabled={createSaving}>
                  x
                </button>
              </div>

              <form className="venues-modal-form" onSubmit={handleCreateTicketType}>
                <div className="form-group">
                  <label htmlFor="ticket-type-name">Ticket Type</label>
                  <input
                    id="ticket-type-name"
                    type="text"
                    value={createName}
                    onChange={(event) => setCreateName(event.target.value)}
                    disabled={createSaving}
                    required
                  />
                </div>

                {createError && <div className="notification error">{createError}</div>}

                <div className="venues-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeCreateModal} disabled={createSaving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={createSaving}>
                    {createSaving ? 'Saving...' : 'Save Ticket Type'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default TicketTypesPage;
